"use client";

import React, { useMemo } from "react";
import { UniversalEntityPage } from "@/components/shared/UniversalEntityPage";
import { useTaskHooks } from "@/lib/hooks/api/shared/graphql-hooks";
import AutoImportModal from "@/components/shared/modals/AutoImportModal";

import { getEntityTableConfig } from "@/lib/entity-registry";
import type { Contact_Tasks } from "@/src/gql/graphql";

const TASK_CONFIG = getEntityTableConfig<Contact_Tasks>("tasks");
import { TaskTemplateSelector } from "@/features/tasks/components/TaskTemplateSelector";
import { SaveTemplateModal } from "@/features/tasks/components/SaveTemplateModal";

import type { EntityFormProps } from "@/components/shared/UniversalEntityPage/types";

export interface DealTasksProps {
  dealId: string;
  dealTitle?: string | null;
}

export const DealTasks: React.FC<DealTasksProps> = ({ dealId, dealTitle }) => {
  const taskHooks = useTaskHooks();

  const customFilters = useMemo(
    () => ({
      entity_type: "deals",
      entity_id: dealId,
    }),
    [dealId]
  );

  const dealTaskConfig = useMemo(
    () => ({
      ...TASK_CONFIG,
      defaultFilters: {
        entity_type: "deals",
        entity_id: dealId,
      },
      displayName: dealTitle?.trim()
        ? `Tasks for ${dealTitle.trim()}`
        : "Deal tasks",
    }),
    [dealId, dealTitle]
  );

  const entityFormProps: EntityFormProps<Contact_Tasks> = useMemo(
    () => ({
      templateSelector: TaskTemplateSelector,
      saveTemplateModal: SaveTemplateModal,
    }),
    []
  );

  const customComponents = useMemo(
    () => ({
      importModal: (props: { onSuccess?: () => void; triggerButton: React.ReactNode }) => (
        <AutoImportModal config={TASK_CONFIG} hooks={taskHooks} {...props} />
      ),
    }),
    []
  );

  const title = dealTitle?.trim() ? `Tasks — ${dealTitle.trim()}` : "Deal tasks";

  return (
    <UniversalEntityPage
      entityName="tasks"
      config={dealTaskConfig}
      hooks={taskHooks}
      filterPreset="all"
      title={title}
      description="View and manage tasks linked to this deal"
      storageKey={`deal-${dealId}-tasks-column-preferences`}
      customFilters={customFilters}
      customComponents={customComponents}
      entityFormProps={entityFormProps}
      showHeader={false}
    />
  );
};
